import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const BrandingContext = createContext(null);

// Raw backend origin — no /api/v1 suffix
const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/v1\/?$/, '').replace(/\/$/, '');

// Builds a full URL from a stored path like /api/uploads/branding/xxx.png
export function buildBrandingUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${BACKEND_ORIGIN}${path}`;
}

const DEFAULT_BRANDING = {
  brand_name: 'WingsDiet',
  slogan: '',
  company_name: '',
  website: '',
  admin_panel_url: '',
  logo_url: '',
  favicon_url: '',
  app_logo: '',
  app_logo_mobile: '',
  app_favicon: '',
};

function applyFavicon(href) {
  if (!href) return;
  const head = document.head;
  let link = head.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    head.appendChild(link);
  }
  link.href = href;
}

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const hostname = encodeURIComponent(window.location.hostname);
      const res = await fetch(`${BACKEND_ORIGIN}/api/v1/public/branding?hostname=${hostname}`, { cache: 'no-store' });
      const data = await res.json();
      const next = { ...DEFAULT_BRANDING, ...(data || {}) };
      setBranding(next);

      // Page title = App Name only
      document.title = next.brand_name || 'WingsDiet';

      // Favicon: prefer new app_favicon, fallback to legacy
      const faviconPath = next.app_favicon || next.favicon_url;
      if (faviconPath) applyFavicon(buildBrandingUrl(faviconPath));
    } catch {
      setBranding(DEFAULT_BRANDING);
      document.title = 'WingsDiet';
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => { if (mounted) await refresh(); };
    run();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ branding, setBranding, loading, refresh }), [branding, loading]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
};
